/**
 * WYMS Collection Grid Logic
 * Handles: AJAX Filters & Sorting, Promo cards, Dynamic Grid Reflow,
 *          Mobile Filter Drawer, custom Type (card-title) filter
 */

const STYLE_FILTER_PARAM = 'filter_style';
const FILTERED_OUT_CLASS = 'is-style-filtered-out';

class WymsCollectionFilters {
  constructor() {
    this.gridSelector        = '.wyms-collection-grid__rows';
    this.filterSelector      = '.js-collection-filters';
    this.countSelector       = '.js-collection-count';
    this.formSelector        = '#FacetsFilterForm';
    this.sectionRootSelector = '.wyms-collection-grid';
    this.mobileContentSelector = '.wyms-mobile-drawer__content';

    /** @type {HTMLElement | null} */
    this.sectionRoot = document.querySelector(this.sectionRootSelector);
    this.sectionInstanceId = this.sectionRoot?.getAttribute('data-section-id') || null;

    /** @type {AbortController | null} */
    this._abortController = null;
    /** @type {number} */
    this._requestId = 0;

    this._onChange = () => this.updateFilters();
    /** @param {MouseEvent} e */
    this._onOutsideClick = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || !target.closest('.facet-details')) {
        document.querySelectorAll('.facet-details[open]').forEach(el => el.removeAttribute('open'));
      }
    };

    /** @param {MouseEvent} e */
    this._onDelegatedClick = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const reset = target?.closest('.js-facet-reset');
      if (!reset) return;
      e.preventDefault();
      e.stopPropagation();
      this.resetFilters(reset instanceof HTMLAnchorElement ? reset : null);
    };

    // Capture phase so reset wins before outside-click closes/interferes
    document.addEventListener('click', this._onDelegatedClick, true);
    document.addEventListener('click', this._onOutsideClick);
    window.addEventListener('popstate', () => this.updateFilters(window.location.search, false));

    this.bindForm();
    this.bindDetailsToggle();
  }

  bindForm() {
    /** @type {HTMLFormElement | null} */
    const form = /** @type {HTMLFormElement | null} */ (document.querySelector(this.formSelector));
    if (!form) return;
    form.onchange = this._onChange;
  }

  /**
   * Clear all active filters in one shot.
   * Updates the URL immediately so the first click always wins (no stale filter_style).
   * @param {HTMLAnchorElement | null} [resetLink]
   */
  /**
   * Query params that must survive filter reset/apply (e.g. search q + type).
   * @returns {URLSearchParams}
   */
  getPreservedParams() {
    const keys = (this.sectionRoot?.dataset.preserveParams || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const current = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    keys.forEach((key) => {
      current.getAll(key).forEach((value) => {
        if (value) out.append(key, value);
      });
    });
    return out;
  }

  resetFilters(resetLink = null) {
    /** @type {HTMLFormElement | null} */
    const form = /** @type {HTMLFormElement | null} */ (document.querySelector(this.formSelector));

    // Clear checkbox state before any FormData-based path can re-read it
    form?.querySelectorAll('input[type="checkbox"]').forEach((inputEl) => {
      /** @type {HTMLInputElement} */ (inputEl).checked = false;
    });

    const href = resetLink?.getAttribute('href') || '';
    const hrefQs = href.includes('?') ? href.split('?')[1] : '';
    const hrefParams = new URLSearchParams(hrefQs);
    const defaultSort = this.sectionRoot?.dataset.defaultSort || 'manual';
    const sortBy =
      hrefParams.get('sort_by') ||
      new URLSearchParams(window.location.search).get('sort_by') ||
      defaultSort;

    const qsParams = this.getPreservedParams();
    qsParams.set('sort_by', sortBy);
    const qs = qsParams.toString();
    const nextUrl = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;

    // Push clean URL first — section Liquid/query parsing must not see old filter_style
    history.pushState({ path: nextUrl }, '', nextUrl);
    this.updateFilters(qs, false);
  }

  bindDetailsToggle() {
    document.querySelectorAll('.facet-details').forEach((detailsEl) => {
      // @ts-ignore
      const details = /** @type {any} */ (detailsEl);
      if (details._wymsToggleBound) return;
      details._wymsToggleBound = true;

      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOpen = details.hasAttribute('open');

        document.querySelectorAll('.facet-details[open]').forEach(other => {
          if (other !== details) other.removeAttribute('open');
        });

        if (isOpen) {
          details.removeAttribute('open');
        } else {
          details.setAttribute('open', '');
        }
      });
    });
  }

  /**
   * @param {string | null} customParams
   * @param {boolean} pushToHistory
   */
  async updateFilters(customParams = null, pushToHistory = true) {
    document.querySelectorAll('.facet-details[open]').forEach(el => el.removeAttribute('open'));

    /** @type {HTMLFormElement | null} */
    const form = /** @type {HTMLFormElement | null} */ (document.querySelector(this.formSelector));
    if (!form) return;

    /** @type {string} */
    let searchParams;
    if (typeof customParams === 'string') {
      searchParams = customParams.replace(/^\?/, '');
    } else {
      const fd = new FormData(form);
      const usp = new URLSearchParams();
      fd.forEach((value, key) => usp.append(key, String(value)));
      searchParams = usp.toString();
    }

    const url = searchParams
      ? `${window.location.pathname}?${searchParams}`
      : window.location.pathname;

    // Update history before fetch so URL state matches the request on first interaction
    if (pushToHistory && window.location.pathname + window.location.search !== url) {
      history.pushState({ path: url }, '', url);
    }

    /** @type {HTMLElement | null} */
    const grid = /** @type {HTMLElement | null} */ (document.querySelector(this.gridSelector));
    if (grid) {
      grid.style.transition = 'opacity 0.2s ease';
      grid.style.opacity = '0.4';
      grid.setAttribute('aria-busy', 'true');
    }

    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    const requestId = ++this._requestId;

    try {
      const sectionId = this.sectionInstanceId || 'wyms-collection-grid';
      const fetchUrl = searchParams
        ? `${url}&section_id=${encodeURIComponent(String(sectionId))}`
        : `${url}?section_id=${encodeURIComponent(String(sectionId))}`;

      const response = await fetch(fetchUrl, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Network response was not ok');
      if (requestId !== this._requestId) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      const newGrid = html.querySelector(this.gridSelector);
      /** @type {HTMLElement | null} */
      const curGrid = /** @type {HTMLElement | null} */ (document.querySelector(this.gridSelector));
      if (newGrid && curGrid) curGrid.innerHTML = newGrid.innerHTML;

      const newFilters = html.querySelector(this.filterSelector);
      /** @type {HTMLElement | null} */
      const curFilters = /** @type {HTMLElement | null} */ (document.querySelector(this.filterSelector));
      if (newFilters && curFilters) {
        newFilters.querySelectorAll('details[open]').forEach(d => d.removeAttribute('open'));
        curFilters.innerHTML = newFilters.innerHTML;
      }

      // Keep product count in sync with Liquid (AJAX does not remount the header shell)
      const newCount = html.querySelector(this.countSelector);
      const curCount = document.querySelector(this.countSelector);
      if (newCount && curCount) {
        curCount.textContent = newCount.textContent;
        const total = newCount.getAttribute('data-total-count');
        const label = newCount.getAttribute('data-count-label');
        if (total) curCount.setAttribute('data-total-count', total);
        if (label) curCount.setAttribute('data-count-label', label);
      }

      const newMobile = html.querySelector(this.mobileContentSelector);
      const curMobile = document.querySelector(this.mobileContentSelector);
      if (newMobile && curMobile) {
        curMobile.innerHTML = newMobile.innerHTML;
        window._wymsMobileDrawer?.rebindAfterAjax?.();
      }

      this.bindForm();
      this.bindDetailsToggle();
      this.syncSortFromUrl();
      syncStyleTitleFilterUi();

      if (typeof initPromoCards === 'function') initPromoCards();
      if (typeof wymsReflowRows === 'function') wymsReflowRows();

    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('[WymsFilters] AJAX error:', err);
    } finally {
      if (requestId !== this._requestId) return;
      /** @type {HTMLElement | null} */
      const g = /** @type {HTMLElement | null} */ (document.querySelector(this.gridSelector));
      if (g) {
        g.style.opacity = '1';
        g.removeAttribute('aria-busy');
      }
    }
  }

  syncSortFromUrl() {
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    /** @type {HTMLFormElement | null} */
    const form = /** @type {HTMLFormElement | null} */ (document.querySelector(this.formSelector));
    if (!sortBy || !form) return;
    form.querySelectorAll('input[name="sort_by"]').forEach((rEl) => {
      const r = /** @type {HTMLInputElement} */ (rEl);
      r.checked = (r.value === sortBy);
    });
  }
}

/**
 * Sync Type-filter UI from the URL.
 * Liquid owns product filtering; this keeps checkboxes/badge aligned and
 * applies a defensive client-side hide if unmatched cards remain in the DOM.
 */
function syncStyleTitleFilterUi() {
  const selected = new URLSearchParams(window.location.search)
    .getAll(STYLE_FILTER_PARAM)
    .filter(Boolean);
  const selectedSet = new Set(selected);

  document.querySelectorAll(`input[name="${STYLE_FILTER_PARAM}"]`).forEach((inputEl) => {
    const input = /** @type {HTMLInputElement} */ (inputEl);
    input.checked = selectedSet.has(input.value);
  });

  const typeDetails = document.querySelector('.js-style-title-filter');
  const labelWrap = typeDetails?.querySelector('.facet-btn__content');
  let countEl = typeDetails?.querySelector('.facet-btn__count');
  if (labelWrap) {
    if (selected.length > 0) {
      if (countEl) {
        countEl.textContent = String(selected.length);
        countEl.setAttribute('aria-label', `${selected.length} selected`);
      } else {
        countEl = document.createElement('span');
        countEl.className = 'facet-btn__count';
        countEl.textContent = String(selected.length);
        countEl.setAttribute('aria-label', `${selected.length} selected`);
        labelWrap.appendChild(countEl);
      }
    } else if (countEl) {
      countEl.remove();
    }
  }

  /** @type {HTMLElement[]} */
  const cards = /** @type {HTMLElement[]} */ ([
    ...document.querySelectorAll('.wyms-product-card[data-style-title]'),
  ]);
  if (!cards.length) return;

  let visibleCount = 0;
  cards.forEach((card) => {
    const title = card.getAttribute('data-style-title') || '';
    const match = selected.length === 0 || selectedSet.has(title);
    card.classList.toggle(FILTERED_OUT_CLASS, !match);
    card.toggleAttribute('hidden', !match);
    if (match) visibleCount += 1;

    const featureRow = card.closest('.wyms-collection-grid__row--feature');
    if (featureRow instanceof HTMLElement) {
      featureRow.classList.toggle(FILTERED_OUT_CLASS, !match);
      featureRow.toggleAttribute('hidden', !match);
    }
  });

  document.querySelectorAll('.wyms-collection-grid__row--default').forEach((rowEl) => {
    const row = /** @type {HTMLElement} */ (rowEl);
    const hasVisible = [...row.querySelectorAll('.wyms-product-card')].some(
      (c) => !/** @type {HTMLElement} */ (c).classList.contains(FILTERED_OUT_CLASS)
    );
    row.classList.toggle(FILTERED_OUT_CLASS, !hasVisible);
    row.toggleAttribute('hidden', !hasVisible);
  });

  const countNode = document.querySelector('.js-collection-count');
  if (countNode instanceof HTMLElement && selected.length > 0) {
    const label = countNode.getAttribute('data-count-label') || 'Artikel';
    countNode.textContent = `${visibleCount} ${label}`;
  }
}

/** @deprecated Use syncStyleTitleFilterUi — kept as alias for safety */
function applyStyleTitleFilter() {
  syncStyleTitleFilterUi();
}

/* -----------------------------------
   Promo Cards
----------------------------------- */
function initPromoCards() {
  document.querySelectorAll('.wyms-card-insert--promo').forEach(card => {
    const btn = card.querySelector('.wyms-card-insert__btn');
    if (!btn) return;
    const fresh = /** @type {HTMLElement} */ (card.cloneNode(true));
    card.replaceWith(fresh);
    fresh.addEventListener('click', e => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || !target.closest('.wyms-card-insert__btn')) {
        /** @type {HTMLElement | null} */
        const innerBtn = /** @type {HTMLElement | null} */ (fresh.querySelector('.wyms-card-insert__btn'));
        innerBtn?.click();
      }
    });
  });
}

/* -----------------------------------
   Mobile list / grid view toggle
----------------------------------- */
const MOBILE_VIEW_STORAGE_KEY = 'wyms-collection-mobile-view';

function getMobileCollectionView() {
  /** @type {HTMLElement | null} */
  const root = document.querySelector('.wyms-collection-grid');
  const fromDom = root?.dataset.mobileView;
  if (fromDom === 'grid' || fromDom === 'list') return fromDom;
  return 'list';
}

/**
 * @param {'list' | 'grid'} view
 * @param {{ persist?: boolean, reflow?: boolean }} [options]
 */
function setMobileCollectionView(view, options = {}) {
  const { persist = true, reflow = true } = options;
  /** @type {HTMLElement | null} */
  const root = document.querySelector('.wyms-collection-grid');
  const toggle = document.querySelector('.js-view-toggle');
  if (!root) return;

  root.dataset.mobileView = view;

  if (toggle instanceof HTMLElement) {
    toggle.dataset.active = view;
  }

  toggle?.querySelectorAll('[data-view]').forEach((btnEl) => {
    const btn = /** @type {HTMLButtonElement} */ (btnEl);
    const active = btn.dataset.view === view;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  if (persist) {
    try {
      localStorage.setItem(MOBILE_VIEW_STORAGE_KEY, view);
    } catch (_) {
      /* private mode / blocked storage */
    }
  }

  if (reflow && typeof wymsReflowRows === 'function') {
    wymsReflowRows();
  }
}

function initMobileViewToggle() {
  const toggle = document.querySelector('.js-view-toggle');
  /** @type {HTMLElement | null} */
  const root = document.querySelector('.wyms-collection-grid');
  if (!toggle || !root) return;

  let initial = 'list';
  try {
    const saved = localStorage.getItem(MOBILE_VIEW_STORAGE_KEY);
    if (saved === 'grid' || saved === 'list') initial = saved;
  } catch (_) {
    /* ignore */
  }

  setMobileCollectionView(/** @type {'list' | 'grid'} */ (initial), {
    persist: false,
    reflow: false,
  });

  if (toggle.dataset.wymsBound === '1') return;
  toggle.dataset.wymsBound = '1';

  toggle.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const btn = target?.closest('[data-view]');
    if (!(btn instanceof HTMLElement)) return;
    const view = btn.dataset.view;
    if (view !== 'list' && view !== 'grid') return;
    if (view === getMobileCollectionView()) return;
    setMobileCollectionView(view);
  });
}

/* -----------------------------------
   Grid Reflow
----------------------------------- */

/**
 * @param {HTMLElement} featureRow
 * @returns {HTMLElement[]}
 */
function getPrecedingDefaultCards(featureRow) {
  /** @type {HTMLElement[]} */
  const precedingDefaults = [];
  let prev = featureRow.previousElementSibling;
  while (prev instanceof HTMLElement && prev.classList.contains('wyms-collection-grid__row--default')) {
    precedingDefaults.unshift(prev);
    prev = prev.previousElementSibling;
  }

  /** @type {HTMLElement[]} */
  const precedingCards = [];
  precedingDefaults.forEach((row) => {
    precedingCards.push(
      ...Array.from(row.children)
        .filter((n) => n instanceof HTMLElement && n.classList.contains('wyms-product-card'))
        .map((n) => /** @type {HTMLElement} */ (n))
    );
  });

  return precedingCards;
}

/**
 * Move cards temporarily parked for mobile grid back into default rows
 * before their feature break (list / desktop restore).
 */
function restoreParkedGridOrphans() {
  /** @type {HTMLElement | null} */
  const rowsContainer = /** @type {HTMLElement | null} */ (document.querySelector('.wyms-collection-grid__rows'));
  if (!rowsContainer) return;

  rowsContainer.querySelectorAll('.wyms-product-card[data-wyms-grid-parked]').forEach((cardEl) => {
    const card = /** @type {HTMLElement} */ (cardEl);
    const parkMode = card.dataset.wymsGridParked;
    const featureLeft = card.closest('.wyms-collection-grid__row--feature.wyms-collection-grid__row--left');
    const afterId = card.dataset.wymsParkAfter || '';
    const featureRight = afterId ? document.getElementById(afterId) : null;
    const oldParent = card.parentElement;

    const host = document.createElement('div');
    host.className = 'wyms-collection-grid__row wyms-collection-grid__row--default';
    host.appendChild(card);

    if (parkMode === 'before-left' && featureLeft?.parentElement) {
      featureLeft.parentElement.insertBefore(host, featureLeft);
    } else if (parkMode === 'after-right' && featureRight?.parentElement) {
      featureRight.parentElement.insertBefore(host, featureRight);
    } else {
      rowsContainer.appendChild(host);
    }

    delete card.dataset.wymsGridParked;
    delete card.dataset.wymsParkAfter;

    if (
      oldParent instanceof HTMLElement &&
      oldParent.classList.contains('wyms-collection-grid__row--default') &&
      !oldParent.children.length
    ) {
      oldParent.remove();
    }
  });
}

/**
 * Avoid empty cells before full-width feature breaks in mobile grid view.
 * - feature--left: park orphan after insert (pairs with feature product)
 * - feature--right: park orphan after the whole feature row
 */
function parkGridOrphansForMobileGrid() {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  if (!isMobile || getMobileCollectionView() !== 'grid') return;

  /** @type {HTMLElement | null} */
  const rowsContainer = /** @type {HTMLElement | null} */ (document.querySelector('.wyms-collection-grid__rows'));
  if (!rowsContainer) return;

  Array.from(rowsContainer.children)
    .filter((n) => n instanceof HTMLElement)
    .forEach((nodeEl, index) => {
      const featureRow = /** @type {HTMLElement} */ (nodeEl);
      if (!featureRow.classList.contains('wyms-collection-grid__row--feature')) return;

      const precedingCards = getPrecedingDefaultCards(featureRow);
      if (precedingCards.length % 2 === 0) return;

      const orphan = precedingCards[precedingCards.length - 1];
      if (!orphan) return;

      if (featureRow.classList.contains('wyms-collection-grid__row--left')) {
        const featureProduct = featureRow.querySelector(
          ':scope > .wyms-product-card:not([data-wyms-grid-parked])'
        );
        if (!featureProduct) return;
        orphan.dataset.wymsGridParked = 'before-left';
        delete orphan.dataset.wymsParkAfter;
        featureRow.insertBefore(orphan, featureProduct);
      } else if (featureRow.classList.contains('wyms-collection-grid__row--right')) {
        if (!featureRow.id) {
          featureRow.id = `wyms-feature-right-${index}`;
        }
        const oldParent = orphan.parentElement;
        const host = document.createElement('div');
        host.className = 'wyms-collection-grid__row wyms-collection-grid__row--default';
        orphan.dataset.wymsGridParked = 'after-right';
        orphan.dataset.wymsParkAfter = featureRow.id;
        host.appendChild(orphan);
        featureRow.insertAdjacentElement('afterend', host);
        if (
          oldParent instanceof HTMLElement &&
          oldParent.classList.contains('wyms-collection-grid__row--default') &&
          !oldParent.children.length
        ) {
          oldParent.remove();
        }
      }

      // Drop empty default rows left behind
      let prev = featureRow.previousElementSibling;
      while (prev instanceof HTMLElement && prev.classList.contains('wyms-collection-grid__row--default')) {
        const row = prev;
        prev = prev.previousElementSibling;
        if (!row.children.length) row.remove();
      }
    });
}

function wymsReflowRows() {
  const isMobile    = window.matchMedia('(max-width: 767px)').matches;
  const isTablet    = window.matchMedia('(max-width: 1023px)').matches && !isMobile;
  // Mobile grid uses CSS display:contents flow; keep 1/row so list↔grid
  // restore is stable. Tablet/desktop still pack by breakpoint.
  const itemsPerRow = isMobile ? 1 : isTablet ? 2 : 3;

  /** @type {HTMLElement | null} */
  const rowsContainer = /** @type {HTMLElement | null} */ (document.querySelector('.wyms-collection-grid__rows'));
  if (!rowsContainer) return;

  restoreParkedGridOrphans();

  /** @type {HTMLElement[]} */
  let segmentCards = [];
  /** @type {HTMLElement[]} */
  let segmentDefaultRows = [];

  const flushSegment = () => {
    if (!segmentDefaultRows.length) { segmentCards = []; segmentDefaultRows = []; return; }
    const frag = document.createDocumentFragment();
    for (let i = 0; i < segmentCards.length; i += itemsPerRow) {
      const row = document.createElement('div');
      row.className = 'wyms-collection-grid__row wyms-collection-grid__row--default';
      for (let j = 0; j < itemsPerRow; j++) {
        const card = segmentCards[i + j];
        if (card) row.appendChild(card);
      }
      frag.appendChild(row);
    }
    const firstRow = segmentDefaultRows[0] || null;
    rowsContainer.insertBefore(frag, firstRow);
    segmentDefaultRows.forEach(r => r.remove());
    segmentCards = []; segmentDefaultRows = [];
  };

  Array.from(rowsContainer.children)
    .filter((n) => n instanceof HTMLElement)
    .forEach((nodeEl) => {
      const node = /** @type {HTMLElement} */ (nodeEl);
      if (node.classList.contains('wyms-collection-grid__row--default')) {
        segmentDefaultRows.push(node);
        segmentCards.push(
          ...Array.from(node.children)
            .filter((n) => n instanceof HTMLElement)
            .map((n) => /** @type {HTMLElement} */ (n))
        );
      } else if (node.classList.contains('wyms-collection-grid__row--feature')) {
        flushSegment();
      }
    });
  flushSegment();

  parkGridOrphansForMobileGrid();
}

// Repack only when a responsive breakpoint changes. Card widths are handled by
// CSS, so rebuilding the row DOM on every resize frame is unnecessary work.
const wymsMobileBreakpoint = window.matchMedia('(max-width: 767px)');
const wymsTabletBreakpoint = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
const wymsHandleBreakpointChange = () => wymsReflowRows();

if (typeof wymsMobileBreakpoint.addEventListener === 'function') {
  wymsMobileBreakpoint.addEventListener('change', wymsHandleBreakpointChange);
  wymsTabletBreakpoint.addEventListener('change', wymsHandleBreakpointChange);
} else {
  wymsMobileBreakpoint.addListener(wymsHandleBreakpointChange);
  wymsTabletBreakpoint.addListener(wymsHandleBreakpointChange);
}

/* ============================================================
   MOBILE DRAWER
   ============================================================ */

class WymsMobileDrawer {
  constructor() {
    /** @type {HTMLElement | null} */
    this.drawer  = /** @type {HTMLElement | null} */ (document.querySelector('.js-mobile-drawer'));
    /** @type {HTMLElement | null} */
    this.overlay = /** @type {HTMLElement | null} */ (document.querySelector('.js-mobile-drawer-overlay'));

    if (!this.drawer || !this.overlay) return;

    // Pending state that will be applied on "Anwenden"
    /** @type {Record<string, string[]>} */
    this._pending = {};

    this._initState();
    this._bindOpen();
    this._bindClose();
    this._bindRows();
    this._bindFooter();
    this._bindResizeClose();
  }

  /* ---------- initialise pending state from current URL ---------- */
  _initState() {
    // For chips we need labels, not values.
    // We read checked state directly from DOM (Liquid already marks active ones).

    // sort chip — read from already-checked radio label
    const checkedRadio = /** @type {HTMLInputElement | null} */ (this.drawer.querySelector('input[type="radio"]:checked'));
    const sortChipEl = this.drawer.querySelector('.js-mobile-sort-chip');
    if (sortChipEl && checkedRadio) {
      sortChipEl.textContent = checkedRadio.closest('.wyms-mobile-filter-checkbox-label')
        ?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent?.trim() || '';
    }

    // filter chips — build from checked checkboxes' label text
    this.drawer.querySelectorAll('.js-mobile-filter-row[data-filter-index]').forEach((rowEl) => {
      const row = /** @type {HTMLElement} */ (rowEl);
      const idx = row.dataset.filterIndex;
      const drop = this.drawer.querySelector(`.js-mobile-filter-dropdown[data-filter-index="${idx}"]`);
      if (!drop) return;

      /** @type {string[]} */
      const selectedLabels = [];
      drop.querySelectorAll('input[type="checkbox"]:checked').forEach((inputEl) => {
        const label = inputEl.closest('.wyms-mobile-filter-checkbox-label')
          ?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent?.trim() || '';
        if (label) selectedLabels.push(label);
      });

      const chipsWrap = row.querySelector('.js-mobile-filter-chips');
      if (chipsWrap) this._renderChips(chipsWrap, selectedLabels);
    });

    // Also populate _pending from URL for _apply() to work correctly
    const usp = new URLSearchParams(window.location.search);
    const urlSort = usp.get('sort_by');
    if (urlSort) this._pending['sort_by'] = [urlSort];

    this.drawer.querySelectorAll('.wyms-mobile-filter-dropdown[data-filter-index]').forEach((drop) => {
      drop.querySelectorAll('input[type="checkbox"]').forEach((inputEl) => {
        const input = /** @type {HTMLInputElement} */ (inputEl);
        if (!this._pending[input.name]) this._pending[input.name] = [];
        if (input.checked && !this._pending[input.name].includes(input.value)) {
          this._pending[input.name].push(input.value);
        }
      });
    });
  }

  /* ---------- open drawer ---------- */
  _bindOpen() {
    document.querySelectorAll('.js-open-mobile-filters').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
  }

  open() {
    if (!this.drawer || !this.overlay) return;
    this._syncFromUrl();
    this.overlay.classList.add('is-visible');
    this.drawer.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        this.drawer.classList.add('is-open');
      });
    });
  }

  /** Keep mobile checkboxes in sync after desktop AJAX filter updates */
  _syncFromUrl() {
    if (!this.drawer) return;
    const usp = new URLSearchParams(window.location.search);

    this.drawer.querySelectorAll('input[type="checkbox"]').forEach((inputEl) => {
      const input = /** @type {HTMLInputElement} */ (inputEl);
      const vals = usp.getAll(input.name);
      input.checked = vals.includes(input.value);
    });

    const sortBy = usp.get('sort_by');
    if (sortBy) {
      this.drawer.querySelectorAll('input[type="radio"][name="mobile_sort_by"]').forEach((rEl) => {
        const r = /** @type {HTMLInputElement} */ (rEl);
        r.checked = r.value === sortBy;
      });
    }

    this._pending = {};
    if (sortBy) this._pending['sort_by'] = [sortBy];
    this.drawer.querySelectorAll('.wyms-mobile-filter-dropdown input[type="checkbox"]').forEach((inputEl) => {
      const input = /** @type {HTMLInputElement} */ (inputEl);
      if (!this._pending[input.name]) this._pending[input.name] = [];
      if (input.checked) this._pending[input.name].push(input.value);
    });
    const checkedRadio = /** @type {HTMLInputElement | null} */ (
      this.drawer.querySelector('input[type="radio"][name="mobile_sort_by"]:checked')
    );
    if (checkedRadio && !this._pending['sort_by']) {
      this._pending['sort_by'] = [checkedRadio.value];
    }

    this._refreshAllChips();
  }

  /* ---------- close drawer ---------- */
  _bindClose() {
    document.querySelectorAll('.js-close-mobile-filters').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
    this.overlay.addEventListener('click', () => this.close());
  }

  close() {
    if (!this.drawer || !this.overlay) return;

    // close all open dropdowns
    this.drawer.querySelectorAll('.wyms-mobile-filter-dropdown.is-open').forEach(d => d.classList.remove('is-open'));

    this.overlay.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';

    const onEnd = () => {
      this.overlay.classList.remove('is-visible');
      this.drawer.classList.remove('is-visible');
      this.drawer.removeEventListener('transitionend', onEnd);
    };
    this.drawer.addEventListener('transitionend', onEnd);
  }

  /** Re-wire drawer content after AJAX replaces mobile filter markup */
  rebindAfterAjax() {
    this._pending = {};
    this._bindRows();
    this._syncFromUrl();
  }

  /* ---------- filter rows (accordion) ---------- */
  _bindRows() {
    if (!this.drawer) return;

    this.drawer.querySelectorAll('.js-mobile-filter-row').forEach((rowEl) => {
      const row = /** @type {HTMLElement} */ (rowEl);
      if (row.dataset.wymsBound === '1') return;
      row.dataset.wymsBound = '1';
      const type = row.dataset.type;

      row.addEventListener('click', () => {
        if (type === 'sort') {
          const drop = this.drawer.querySelector('.js-mobile-sort-dropdown');
          this._toggleDropdown(drop, row);
        } else {
          const idx = row.dataset.filterIndex;
          const drop = this.drawer.querySelector(`.js-mobile-filter-dropdown[data-filter-index="${idx}"]`);
          this._toggleDropdown(drop, row);
        }
      });
    });

    this.drawer.querySelectorAll('.js-mobile-sort-dropdown input[type="radio"]').forEach((inputEl) => {
      const input = /** @type {HTMLInputElement} */ (inputEl);
      if (input.dataset.wymsBound === '1') return;
      input.dataset.wymsBound = '1';
      input.addEventListener('change', () => {
        this._pending['sort_by'] = [input.value];
        const chip = this.drawer.querySelector('.js-mobile-sort-chip');
        if (chip) {
          chip.textContent = input.closest('.wyms-mobile-filter-checkbox-label')
            ?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent || '';
        }
      });
    });

    this.drawer.querySelectorAll('.js-mobile-filter-dropdown[data-filter-index] input[type="checkbox"]').forEach((inputEl) => {
      const input = /** @type {HTMLInputElement} */ (inputEl);
      if (input.dataset.wymsBound === '1') return;
      input.dataset.wymsBound = '1';
      input.addEventListener('change', () => {
        const name = input.name;
        if (!this._pending[name]) this._pending[name] = [];
        if (input.checked) {
          if (!this._pending[name].includes(input.value)) {
            this._pending[name].push(input.value);
          }
        } else {
          this._pending[name] = this._pending[name].filter(v => v !== input.value);
        }

        const drop = input.closest('.js-mobile-filter-dropdown');
        const idx  = drop?.getAttribute('data-filter-index');
        if (idx !== null && idx !== undefined) {
          const row = this.drawer.querySelector(`.js-mobile-filter-row[data-filter-index="${idx}"]`);
          if (row) this._refreshRowChips(row, name);
        }
      });
    });
  }

  /**
   * Toggle a dropdown open/closed, closing others first.
   * @param {Element | null} drop
   * @param {HTMLElement} row
   */
  _toggleDropdown(drop, row) {
    if (!drop) return;
    const isOpen = drop.classList.contains('is-open');

    // close all
    this.drawer.querySelectorAll('.wyms-mobile-filter-dropdown.is-open').forEach(d => {
      if (d !== drop) d.classList.remove('is-open');
    });

    if (isOpen) {
      drop.classList.remove('is-open');
    } else {
      drop.classList.add('is-open');
    }
  }

  /* ---------- footer buttons ---------- */
  _bindFooter() {
    const resetBtn = this.drawer?.querySelector('.js-mobile-drawer-reset');
    const applyBtn = this.drawer?.querySelector('.js-mobile-drawer-apply');

    resetBtn?.addEventListener('click', () => this._reset());
    applyBtn?.addEventListener('click', () => this._apply());
  }

  _reset() {
    // uncheck all checkboxes
    this.drawer.querySelectorAll('input[type="checkbox"]').forEach((inputEl) => {
      /** @type {HTMLInputElement} */ (inputEl).checked = false;
    });

    // reset sort to first radio
    const firstRadio = /** @type {HTMLInputElement | null} */ (this.drawer.querySelector('input[type="radio"]'));
    if (firstRadio) {
      this.drawer.querySelectorAll('input[type="radio"]').forEach(r => /** @type {HTMLInputElement} */ (r).checked = false);
      firstRadio.checked = true;
      this._pending['sort_by'] = [firstRadio.value];
      const chip = this.drawer.querySelector('.js-mobile-sort-chip');
      if (chip) chip.textContent = firstRadio.closest('.wyms-mobile-filter-checkbox-label')?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent || '';
    }

    // clear pending filters
    Object.keys(this._pending).forEach(k => {
      if (k !== 'sort_by') this._pending[k] = [];
    });

    this._refreshAllChips();
  }

  _apply() {
    const usp = new URLSearchParams();

    // sort
    const sortVal = (this._pending['sort_by'] || [])[0];
    if (sortVal) usp.set('sort_by', sortVal);

    // filters
    Object.entries(this._pending).forEach(([key, vals]) => {
      if (key === 'sort_by') return;
      vals.forEach(v => { if (v) usp.append(key, v); });
    });

    const preserved = window._wymsFiltersInstance?.getPreservedParams?.();
    if (preserved) {
      preserved.forEach((value, key) => {
        if (!usp.has(key)) usp.set(key, value);
      });
    }

    const url = `${window.location.pathname}?${usp.toString()}`;

    this.close();

    // Trigger AJAX update via the desktop filter instance
    if (window._wymsFiltersInstance) {
      window._wymsFiltersInstance.updateFilters(usp.toString(), true);
    } else {
      window.location.href = url;
    }
  }

  /* ---------- chip rendering ---------- */

  /**
   * Refresh chips for a single filter row by reading checked labels from its dropdown.
   * @param {Element} row
   * @param {string} _name  (unused, kept for API compat)
   */
  _refreshRowChips(row, _name) {
    const chipsWrap = row.querySelector('.js-mobile-filter-chips');
    if (!chipsWrap) return;

    const idx = /** @type {HTMLElement} */ (row).dataset.filterIndex;
    const drop = this.drawer.querySelector(`.js-mobile-filter-dropdown[data-filter-index="${idx}"]`);
    if (!drop) return;

    /** @type {string[]} */
    const selectedLabels = [];
    drop.querySelectorAll('input[type="checkbox"]:checked').forEach((inputEl) => {
      const label = inputEl.closest('.wyms-mobile-filter-checkbox-label')
        ?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent?.trim() || '';
      if (label) selectedLabels.push(label);
    });

    this._renderChips(chipsWrap, selectedLabels);
  }

  /** Refresh all filter rows chips from current DOM checked state */
  _refreshAllChips() {
    if (!this.drawer) return;

    // sort row chip
    const checkedRadio = /** @type {HTMLInputElement | null} */ (this.drawer.querySelector('input[type="radio"]:checked'));
    const sortChip = this.drawer.querySelector('.js-mobile-sort-chip');
    if (sortChip && checkedRadio) {
      sortChip.textContent = checkedRadio.closest('.wyms-mobile-filter-checkbox-label')
        ?.querySelector('.wyms-mobile-filter-checkbox__text')?.textContent?.trim() || '';
    }

    // filter rows
    this.drawer.querySelectorAll('.js-mobile-filter-row[data-filter-index]').forEach((rowEl) => {
      const row = /** @type {HTMLElement} */ (rowEl);
      this._refreshRowChips(row, '');
    });
  }

  /**
   * Renders chips into a chips wrapper, respecting max-2 visible rule with overflow badge.
   * @param {Element} wrap
   * @param {string[]} labels
   */
  _renderChips(wrap, labels) {
    wrap.innerHTML = '';
    if (!labels.length) return;

    const MAX_CHIPS = 2;
    const MAX_CHIP_WIDTH_PX = 100; // rough px cap per chip (controlled by CSS too)

    // We show up to MAX_CHIPS chips. If there are more, we show a +N badge.
    // The overflow logic for "too long" is handled via CSS (max-width + text-overflow on the chip).
    // For the +N count: always show how many did NOT fit (i.e. total - shown).

    const shown = labels.slice(0, MAX_CHIPS);
    const overflow = labels.length - shown.length;

    shown.forEach(label => {
      const chip = document.createElement('span');
      chip.className = 'wyms-mobile-filter-chip';
      chip.textContent = label;
      wrap.appendChild(chip);
    });

    if (overflow > 0) {
      const badge = document.createElement('span');
      badge.className = 'wyms-mobile-filter-chip--overflow';
      badge.textContent = `+${overflow}`;
      wrap.appendChild(badge);
    }
  }
  /** Close drawer automatically when viewport widens to desktop (>= 768px) */
  _bindResizeClose() {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (/** @type {MediaQueryListEvent | MediaQueryList} */ e) => {
      if (e.matches && this.drawer?.classList.contains('is-open')) {
        this.close();
      }
    };
    // Modern browsers
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
    } else {
      // Safari < 14 fallback
      mq.addListener(handler);
    }
  }
}

/* -----------------------------------
   DOMContentLoaded — boot everything
----------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  window._wymsFiltersInstance = new WymsCollectionFilters();
  window._wymsMobileDrawer = new WymsMobileDrawer();
  initPromoCards();
  syncStyleTitleFilterUi();
  initMobileViewToggle();
  wymsReflowRows();
});